import { mergeAttributes } from '@tiptap/core'
import BaseImage, { type ImageOptions } from '@tiptap/extension-image'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import InlineImage from './InlineImage.vue'
import { Plugin, PluginKey } from 'prosemirror-state'

// Declare PI_API_HOST if it's not already declared elsewhere
declare const PI_API_HOST: string

interface PivotImageOptions extends ImageOptions {
  video: boolean
  inline: boolean
  noupload: boolean
  client?: any
}

const Image = BaseImage.extend<PivotImageOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      video: false,
      inline: true,
      noupload: false,
      client: null
    }
  },

  addStorage() {
    return {}
  },

  addCommands() {
    return {
      ...(this.parent ? this.parent() : {}),
      openImageModal: () => () => {
        this.storage.onEditImage?.()
      }
    }
  },

  addAttributes() {
    return {
      ...(this.parent ? this.parent() : {}),
      src: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => {
          const attr = element.getAttribute('src')
          return attr ? attr.replace(`//${PI_API_HOST}`, '') : null
        },
        renderHTML: ({ src }: { src: string | null }) => ({
          src:
            src && !src.startsWith('data:') && !src.startsWith('http')
              ? `//${PI_API_HOST}${src}`
              : src
        })
      },
      width: {},
      height: {}
    }
  },

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }: { editor: any }): boolean => {
        if (editor.isActive('image')) {
          editor.commands.openImageModal()
          return true
        }
        return false
      }
    }
  },

  addNodeView() {
    return VueNodeViewRenderer(InlineImage)
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: any }) {
    return ['img', mergeAttributes(HTMLAttributes)]
  },

  parseHTML() {
    return [{ tag: 'img' }]
  },

  renderText({ node }: { node: any }): string {
    return `[image:${node.attrs.alt ?? 'unknown'}]`
  },

  addProseMirrorPlugins() {
    const options = this.options

    // Function to handle file uploads
    const uploadFile = async (file: File): Promise<string | undefined> => {
      if (!file) {
        throw new Error('You must select a file to insert')
      }
      if (options.noupload) {
        return new Promise<string>(resolve => {
          const reader = new FileReader()
          reader.addEventListener('load', () => {
            resolve(reader.result as string)
          })
          reader.readAsDataURL(file)
        })
      } else {
        const ext = file.name.split('.').pop()
        const name = `${new Date().getTime()}.${ext}`
        const url = await options.client?.assets.upload({
          file,
          name
        })
        return url
      }
    }

    const onPaste = (view: any, event: ClipboardEvent, slice: any): boolean => {
      let handled = false
      const clipboardData =
        event.clipboardData || (event as any).originalEvent.clipboardData
      const items = clipboardData?.items
      if (!items) return false

      const { schema } = view.state
      for (const item of items) {
        const image = item.getAsFile()
        // If not a file, allow pasting text
        if (!image) return false
        event.preventDefault()
        uploadFile(image).then(imageSrc => {
          const node = schema.nodes.image.create({ src: imageSrc })
          const transaction = view.state.tr.replaceSelectionWith(node)
          view.dispatch(transaction)
        })
        handled = true
      }
      return handled
    }

    const onDrop = (view: any, event: DragEvent, slice: any): boolean => {
      try {
        const { files } = event.dataTransfer || { files: [] }
        const hasFiles = files.length > 0
        const imageNode = slice.content?.content?.[0]

        if (!hasFiles && (!imageNode || imageNode.type.name !== 'image')) {
          return false
        }

        const { schema } = view.state
        const coordinates = view.posAtCoords({
          left: event.clientX,
          top: event.clientY
        })

        if (!hasFiles && imageNode) {
          event.preventDefault()
          let imageSrc: string | undefined
          if (imageNode.attrs.src?.startsWith('data:')) {
            const type = imageNode.attrs.src
              .split(';')[0]
              .split(':')[1]
              .split('/')[1]
            const filename = `${new Date().getTime()}.${type}`
            if (options.noupload) {
              imageSrc = imageNode.attrs.src
            } else {
              fetch(imageNode.attrs.src).then(async response => {
                const blob = await response.blob()
                const file = new File([blob], filename, { type: blob.type })
                imageSrc = await uploadFile(file)
                const node = schema.nodes.image.create({ src: imageSrc })
                const transaction = view.state.tr.insert(coordinates.pos, node)
                view.dispatch(transaction)
              })
              return true
            }
          } else if (imageNode.attrs.src?.startsWith('http')) {
            imageSrc = imageNode.attrs.src
          } else {
            imageSrc = ''
          }
          const node = schema.nodes.image.create({ src: imageSrc })
          const transaction = view.state.tr.insert(coordinates.pos, node)
          view.dispatch(transaction)
          return true
        } else {
          for (const file of Array.from(files)) {
            if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
              return false
            }
            event.preventDefault()
            uploadFile(file).then(imageSrc => {
              const node = schema.nodes.image.create({ src: imageSrc })
              const transaction = view.state.tr.insert(coordinates.pos, node)
              view.dispatch(transaction)
            })
          }
          return true
        }
      } catch (error) {
        // flash.error('Error dropping images')
        return true
      }
    }

    return [
      new Plugin({
        key: new PluginKey('image-paste'),
        props: {
          handlePaste: onPaste,
          handleDrop: onDrop
        }
      })
    ]
  }
})

export default Image
